import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useCallback, useContext } from 'react';
import { Typography } from 'antd';
import { Product as ProductType } from 'types/app.types';
import ProductsContext from 'contexts/ProductsContext';
import { createLogger } from 'services/debugLogger';
import ProductsSelector from './components/ProductsSelector';
import styles from './index.module.scss';
import ConfigPanels from './components/ConfigPanel';
import Time from '../../../../../../../../../../../../components/Time';
import MapViewContext from '../../../../../../../../../../../../contexts/MapViewContext';
import { GeoLayer } from '../../../../../../../../../../../../types/geoserver.types';
import TimeSeriesContext from '../../../../../../../../../../../../contexts/TimeSeriesContext';
import MainAppContext from '../../../../../../../../../../../../contexts/MainAppContext';
import { calculateRanges, generateTimestampList } from '../../../../../../../../../../../../utils';

const logger = createLogger('Product');

const { productWrapper, timeWrapper } = styles;
interface ProductProps {
    product: ProductType,
    index: number
}

function Product({ product, index }: ProductProps) {
  const {
    editProduct,
    activateProduct,
    deactivateProduct,
    activeProductsTimes,
    activeProducts,
  } = useContext(ProductsContext);
  const {
    currentPosition, range, setTimestampList, timestampList, stop,
  } = useContext(TimeSeriesContext);
  const { playbackMode } = useContext(MainAppContext);
  const {
    addLayer, removeLayer, replaceLayer, preloadLayer,
  } = useContext(MapViewContext);
  const time = activeProductsTimes[product.index];
  const isActivated = activeProducts.some((index) => index === product.index);

  const handleSelectChange = useCallback((value: string, layer: GeoLayer) => {
    const isDTN = layer.href === 'dtn';
    const newProduct = new ProductType({
      ...product,
      internalName: isDTN ? (layer.path || layer.name) : value,
      workspace: isDTN ? 'DTN' : value.split(':')[0].replace('-', ' '),
      name: isDTN ? layer.name : value.split(':')[1].replace('-', ' '),
      layer,
      currentStyle: layer.defaultStyle?.name || '',
      provider: isDTN ? 'dtn' : undefined,
    });
    editProduct(newProduct);
    replaceLayer(newProduct);
  }, [product, editProduct, replaceLayer]);

  const getTime = () => {
    if (!time) return time;
    return time.isValid() ? time.format('YYYY-MM-DD   HH:mm:ss') : 'No data';
  };

  const handleOnCheckboxChange = (
    { target: { checked } }: CheckboxChangeEvent,
  ) => {
    if (checked) {
      let generatedTimestampList = timestampList;
      activateProduct(index);
      addLayer(product, currentPosition);
      const { samplingDuration } = calculateRanges(range);

      if (timestampList.length === 0) {
        generatedTimestampList = generateTimestampList(
          range,
          samplingDuration,
        );
        setTimestampList(generatedTimestampList);
      }

      preloadLayer({ index, product, generatedTimestampList }).then().catch(
        (error) => { logger.error('Product layer update failed', error); },
      );
    } else {
      deactivateProduct(index);
      removeLayer(product, false);
      // Stop replay if no active products remain after this deactivation
      if (playbackMode && activeProducts.filter((i) => i !== product.index).length === 0) {
        stop();
      }
    }
  };
  return (
    <div className={productWrapper} key={product.id}>
      <Typography.Paragraph style={{
        marginBottom: '0px',
        paddingLeft: '38px',
        fontSize: '12px',
      }}
      >
        {product.workspace}
      </Typography.Paragraph>
      <ProductsSelector
        product={product}
        onSelectChange={handleSelectChange}
        onCheckboxChange={(checked) => handleOnCheckboxChange(checked)}
      />
      <div className={timeWrapper}>
        { isActivated
          ? <Time value={getTime()} showUTC={time?.isValid()} />
          : <Time value={undefined} showUTC={false} /> }
      </div>
      <ConfigPanels product={product} />
    </div>
  );
}

export default Product;
